import type {
  Attendance,
  OccurrenceKey,
  PairOccurrence,
  StudentOccurrences as StudentOccurrencesData,
  StudentPayment,
} from "@edu/shared";
import { occurrenceKeyString, PAYMENT_WINDOW_DAYS } from "@edu/shared";
import { dayCountText, meetingCountText } from "../../lib/plurals.ts";
import { Empty } from "../../lib/Empty.tsx";
import { Loading } from "../../lib/loading.tsx";
import { MonthChips, SettleLine, summaryText } from "./StudentMarks.tsx";
import { StudentOccurrenceRow } from "./StudentOccurrenceRow.tsx";
import { useStudentOccurrences } from "./use-student-occurrences.ts";

/**
 * Занятия пары: одна карточка вместо двух (решение владельца 26.09.2026).
 *
 * До этого дня здесь стояли два списка про одно и то же: «Занятия» из УРОКОВ
 * пары и «Учёт» из ЗАНЯТИЙ расписания за полгода. Правильная единица это
 * занятие: у преподавателя, ведущего уроки в Zoom (режим сосуществования,
 * раздел 2 CLAUDE.md), уроков нет, а занятия есть. Правила учёта (нет
 * тревожного цвета у долга, чипы месяцев без подтверждения, круг посещения)
 * живут в шапке `StudentMarks.tsx` вместе с органами учёта.
 *
 * ⚠️ Список постраничный: карточка отдаёт десять последних, остальные
 * догружаются кнопкой «Показать ещё». Когда список приезжал целиком (до двухсот
 * строк), карточка «Доступ» со ссылкой уезжала под ним на сотни строк вниз.
 * Окна поверх списка учеников при этом по-прежнему нет (решение владельца
 * 13.09.2026), и уводить отсюда некуда: вся история листается здесь же.
 */
export function StudentOccurrences(props: {
  data: StudentOccurrencesData;
  /** Учёт пары. Отсутствие это старый API: тогда и органов учёта нет. */
  payment: StudentPayment | undefined;
  tracking: { attendance: boolean; payments: boolean };
  /** Зона смотрящего: в ней считается предзаполненная дата черты. */
  zone: string;
  studentUserId: string;
  now: number;
  busy: boolean;
  onMark: (key: OccurrenceKey, patch: { paid?: boolean; attendance?: Attendance | null }) => void;
  onSettle: (before: string) => void;
  onMonth: (period: string, paid: boolean) => void;
}) {
  const { payment, tracking } = props;
  const mode = payment?.mode;
  const perLesson = tracking.payments && mode === "per_lesson";
  const list = useStudentOccurrences(
    props.studentUserId,
    { rows: props.data.rows, nextCursor: props.data.nextCursor },
    perLesson,
  );

  const monthly = tracking.payments && mode === "monthly";
  const tracked = payment !== undefined && (tracking.attendance || tracking.payments);
  const summary = tracked ? summaryText(payment, tracking) : "";
  const unpaid = payment?.unpaidCount ?? 0;
  const filtered = list.filter === "unpaid";

  /*
    Будущее над историей, дальнее сверху (порядок сервера): столбец дат идёт
    монотонно через обе части. Под фильтром его нет: занятие, которое ещё не
    прошло, не бывает не оплачено (`countUnpaid`).
  */
  const upcoming = filtered ? [] : props.data.upcoming;
  const nothing = upcoming.length === 0 && list.rows.length === 0 && props.data.count === 0;

  return (
    <section className="card">
      <h2>
        Занятия{" "}
        <span className="muted stu__count">
          {[meetingCountText(props.data.count), summary].filter((t) => t !== "").join(" · ")}
        </span>
      </h2>

      {/*
        Чипы месяцев стоят ВЫШЕ проверки на пустой список занятий и рисуются
        даже тогда, когда занятий ещё не было ни одного. Это не мелочь: при
        предоплате первый платёж приходит РАНЬШЕ первого занятия, и спрятать
        чипы за «занятий пока нет» значило бы спрятать главный сценарий фичи.
      */}
      {monthly && payment !== undefined && (
        <MonthChips
          months={payment.months}
          currentPeriod={payment.currentPeriod}
          busy={props.busy}
          onMonth={props.onMonth}
        />
      )}

      {nothing ? (
        /*
          ⚠️ Дверь ведёт в расписание, и с 17.09.2026 другой у неё быть не
          может. До этого дня плашка звала отметить ученика галочкой в списке и
          вела на «Учеников», а галочки ушли вместе с пачкой уроков: кнопка
          приводила туда, где завести занятие уже нечем. Тупик доставался ровно
          тому, кто заводит первое занятие только что заведённому ученику.

          Сам ученик в адрес окна не передаётся: выбор адресата живёт внутри
          него одной ручкой (`OccurrenceRecipient`), и второй путь к тому же
          полю означал бы два ответа на вопрос «кому».
        */
        <Empty
          icon="calendar"
          title="Занятий пока нет"
          text="Занятия ставятся в расписании: нажмите на свободное время и выберите ученика."
          action={{ label: "Открыть расписание", to: "/schedule" }}
        />
      ) : (
        <>
          {perLesson && (
            <SettleLine
              zone={props.zone}
              now={props.now}
              busy={props.busy}
              onSettle={props.onSettle}
            />
          )}

          {/*
            Фильтр только там, где вопрос «не оплачено» задают: поурочная пара
            при включённом учёте оплат. И только когда есть что отбирать, либо
            он уже нажат: иначе после отметки последнего занятия чип пропал бы
            из-под пальца вместе со способом его отжать.
          */}
          {perLesson && (unpaid > 0 || filtered) && (
            <p className="acts">
              <button
                type="button"
                className="chip"
                aria-pressed={filtered}
                onClick={() => list.setFilter(filtered ? null : "unpaid")}
              >
                Неоплаченные
              </button>
            </p>
          )}

          <OccurrenceList
            upcoming={upcoming}
            rows={list.rows}
            busy={list.busy}
            filtered={filtered}
            attendance={tracked && tracking.attendance}
            payment={tracked && perLesson}
            studentUserId={props.studentUserId}
            now={props.now}
            /*
              ⚠️ Чипы стоят и пока список перечитывается, а не только пока
              идёт сама отметка: строка до ответа показывает СТАРОЕ значение,
              и второй щелчок по «Был» считал бы круг от него
            */
            marking={props.busy || list.busy}
            onMark={props.onMark}
          />

          {list.err !== null && <p className="err">{list.err}</p>}
          {/*
            Кнопка, а не бесконечная прокрутка: на канале 128 кбит/с подгрузка
            «сама» означает подгрузку в момент, когда человек просто проскроллил
            мимо
          */}
          {list.nextCursor !== null && (
            <p className="acts">
              <button className="btn" disabled={list.busy} onClick={list.loadMore}>
                {list.busy ? "Загрузка…" : "Показать ещё"}
              </button>
            </p>
          )}

          {/*
            ⚠️ Форма слова «день» считается от числа, а не зашита в строку
            (14.09.2026, долг подхода 2з): `PAYMENT_WINDOW_DAYS` это константа
            контракта, и зашитое «дней» соврало бы молча на любом значении,
            кончающемся на 1, 2, 3 или 4. Падеж после «за последние»
            винительный, и для этих форм он совпадает с именительным, поэтому
            общий счётчик подходит без своей копии.

            Подсказка только у поурочного учёта: история здесь вся, а
            неоплаченными считается окно, и без неё занятие старше окна без
            чипа оплаты читалось бы как поломка. Слова «долг» на экране нет:
            это наш внутренний словарь, а не то, что преподаватель думает о
            семье ученика.
          */}
          {perLesson && (
            <p className="hint">
              Неоплаченными считаются занятия за последние {dayCountText(PAYMENT_WINDOW_DAYS)}.
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Список строками: сначала будущее, затем история.
 *
 * Набор колонок решается здесь один раз на весь список, вариантом `data-cols`
 * у одного шаблона `list--stu-occ`: у строки своих соображений о колонках быть
 * не должно, иначе шапка и строки разъедутся.
 */
function OccurrenceList(props: {
  upcoming: readonly PairOccurrence[];
  rows: readonly PairOccurrence[];
  busy: boolean;
  filtered: boolean;
  attendance: boolean;
  payment: boolean;
  studentUserId: string;
  now: number;
  marking: boolean;
  onMark: (key: OccurrenceKey, patch: { paid?: boolean; attendance?: Attendance | null }) => void;
}) {
  if (props.upcoming.length === 0 && props.rows.length === 0) {
    // Пусто бывает только под фильтром или пока он грузится: без фильтра
    // пустую историю закрывает плашка со ссылкой в расписание
    if (props.busy) return <Loading />;
    return (
      <p className="muted">
        {props.filtered
          ? `За последние ${dayCountText(PAYMENT_WINDOW_DAYS)} неоплаченных занятий нет`
          : "Занятий пока нет"}
      </p>
    );
  }

  /*
    Идущее занятие поднимается над историей, потому что это единственная
    строка, с которой прямо сейчас что-то делают. Сортировка устойчива, поэтому
    порядок сервера внутри обеих групп сохраняется. Поднимается только среди
    ЗАГРУЖЕННЫХ: идущее, которое старше первой страницы, не ищем, потому что
    настоящий идущий урок свежий.
  */
  const history = [...props.rows].sort(
    (a, b) => Number(b.lesson?.status === "live") - Number(a.lesson?.status === "live"),
  );
  const cols =
    props.attendance && props.payment
      ? "full"
      : props.attendance
        ? "att"
        : props.payment
          ? "pay"
          : undefined;

  const row = (o: PairOccurrence, upcoming: boolean) => (
    <StudentOccurrenceRow
      key={`${upcoming ? "u" : "h"}:${occurrenceKeyString(o.key)}`}
      occurrence={o}
      studentUserId={props.studentUserId}
      now={props.now}
      attendance={props.attendance}
      payment={props.payment}
      upcoming={upcoming}
      busy={props.marking}
      onMark={props.onMark}
    />
  );

  return (
    <ul className="list list--stu-occ" data-cols={cols}>
      <li className="list__head" aria-hidden="true">
        <span>Дата</span>
        <span>Занятие</span>
        {props.attendance && <span>Был</span>}
        {props.payment && <span>Оплата</span>}
        <span />
      </li>
      {props.upcoming.map((o) => row(o, true))}
      {history.map((o) => row(o, false))}
    </ul>
  );
}
